import { useZero } from "@rocicorp/zero/react";
import { styled } from "goober";
import { nanoid } from "nanoid";
import { useRef, useState } from "react";
import type { NetworkDestination } from "../../../../db/schema";
import { sharedMutators } from "../../../../shared/mutators";
import { generateClientCert } from "../../../../shared/tunnel/cert";
import { makeUrl, navigate, routes, useCurrentUrl } from "../../../lib/urls";
import { Button } from "../../Button";
import { Input } from "../../Input";
import { ListTable } from "../../ListTable";
import { TabbedModal } from "../../TabbedModal";
import { Text } from "../../Text";
import {
  Card,
  HoverActions,
  ListBox,
  NeutralIconBadge,
  RowNameContent,
  RowNameLayout,
  SectionHeading,
} from "../shared";
import { DestinationInput } from "./DestinationInput";
import { useDestinationDraft } from "./destinationDraft";
import { NetworkAccessTab } from "./NetworkAccessTab";
import { NetworkProvisioning } from "./NetworkProvisioning";

type Tab = "settings" | "access";

const Sections = styled("div")`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

function destinationLabel(d: NetworkDestination): string {
  return d.type === "cidr" ? d.range : d.pattern;
}

function typeLabel(type: NetworkDestination["type"]): string {
  return type === "hostnameGlob" ? "Hostname pattern" : "CIDR";
}

export function CreateNetworkModal({ onClose }: { onClose: () => void }) {
  const z = useZero();
  const { tab } = useCurrentUrl(routes.settings.networking.new);
  const activeTab = tab as Tab;
  const setActiveTab = (next: Tab) =>
    navigate(routes.settings.networking.new, { tab: next });
  const [name, setName] = useState("");
  const [destinations, setDestinations] = useState<NetworkDestination[]>([]);
  const [newType, setNewType] =
    useState<NetworkDestination["type"]>("hostnameGlob");
  const destinationInputRef = useRef<HTMLInputElement>(null);
  const [createdId, setCreatedId] = useState<string | null>(null);
  const [clientKeyPem, setClientKeyPem] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);

  const trimmedName = name.trim();
  const canCreate =
    trimmedName.length > 0 && destinations.length > 0 && !creating;

  const destinationDraft = useDestinationDraft(newType);

  const addDestination = (next: NetworkDestination) => {
    const label = destinationLabel(next);
    setDestinations((current) =>
      current.some((d) => d.type === next.type && destinationLabel(d) === label)
        ? current
        : [...current, next]
    );
    destinationDraft.setDraft("");
    destinationInputRef.current?.focus();
  };

  const removeDestination = (index: number) => {
    setDestinations((current) => current.filter((_, i) => i !== index));
  };

  const destinationItems = destinations.map((d, index) => ({
    key: `${d.type}:${destinationLabel(d)}`,
    name: (
      <RowNameLayout>
        <NeutralIconBadge iconName="pin" />
        <RowNameContent>
          <Text variant="text-regular-md" ellipsis>
            {destinationLabel(d)}
          </Text>
          <Text variant="text-small" color="var(--text-secondary)" ellipsis>
            {typeLabel(d.type)}
          </Text>
        </RowNameContent>
      </RowNameLayout>
    ),
    actions: (hasMouse: boolean) => (
      <HoverActions visible={hasMouse}>
        <Button
          variant="secondary"
          icon="trash"
          onClick={() => removeDestination(index)}
        />
      </HoverActions>
    ),
  }));

  const handleCreate = async () => {
    if (!canCreate) return;
    setCreating(true);
    const id = nanoid(12);
    try {
      const { certPem, keyPem, sha256Hex } = await generateClientCert(id);
      setClientKeyPem(keyPem);
      const result = z.mutate(
        sharedMutators.createNetwork({
          id,
          name: trimmedName,
          identifier: "tunnel",
          destinations,
          clientCertPem: certPem,
          clientCertSha256: sha256Hex,
        })
      );
      setCreatedId(id);
      await result.server;
    } finally {
      setCreating(false);
    }
  };

  const tabs = [
    { key: "settings", label: "Settings", icon: "settings-2" },
    { key: "access", label: "Access", icon: "lock", disabled: !createdId },
  ] as const;

  const footerRight = !createdId ? (
    <Button size="l" onClick={handleCreate} disabled={!canCreate}>
      {creating ? "Provisioning…" : "Create"}
    </Button>
  ) : activeTab === "settings" ? (
    <Button size="l" onClick={() => setActiveTab("access")}>
      Next
    </Button>
  ) : (
    <Button size="l" onClick={onClose}>
      Done
    </Button>
  );

  return (
    <TabbedModal
      icon={<NeutralIconBadge iconName="earth" />}
      title={createdId ? trimmedName || "Tunnel" : "New tunnel network"}
      tabs={tabs.map((tab) => ({
        ...tab,
        to: makeUrl(routes.settings.networking.new, { tab: tab.key }),
      }))}
      activeTab={activeTab}
      onTabChange={setActiveTab}
      onClose={onClose}
      footerRight={footerRight}
    >
      {activeTab === "access" && createdId ? (
        <NetworkAccessTab networkId={createdId} canManage />
      ) : createdId ? (
        <NetworkProvisioning
          networkId={createdId}
          clientKeyPem={clientKeyPem}
        />
      ) : (
        <Sections>
          <Card>
            <SectionHeading>
              <Text variant="text-regular-md">Name</Text>
              <Text variant="text-small" color="var(--text-secondary)">
                A label for this private network.
              </Text>
            </SectionHeading>
            <Input
              autoFocus
              placeholder="e.g. Production VPC"
              value={name}
              onChange={(e) => setName(e.target.value)}
              style={{ width: "100%" }}
            />
          </Card>

          <Card>
            <SectionHeading>
              <Text variant="text-regular-md">Destinations</Text>
              <Text variant="text-small" color="var(--text-secondary)">
                IP ranges (CIDR) or hostname globs reachable through this
                tunnel.
              </Text>
            </SectionHeading>
            <DestinationInput
              type={newType}
              onTypeChange={setNewType}
              draft={destinationDraft}
              onAdd={addDestination}
              inputRef={destinationInputRef}
            />
            {destinations.length > 0 && (
              <ListBox
                data-compact="true"
                style={{ flex: "none", overflow: "hidden" }}
              >
                <ListTable
                  headers={{ name: "", actions: "" }}
                  hideHeaders
                  items={destinationItems}
                />
              </ListBox>
            )}
          </Card>
        </Sections>
      )}
    </TabbedModal>
  );
}
